import React, { useState, useEffect } from 'react';

// custom components
import { CarrierCodeSelector } from "_molecules";

// bootstrap
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';

// styles 
import { Colors } from '_styles';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectFlightInformation, setFlightInformation } from '_redux/slices/assistance-bookings/booking-form';
import { selectAddFlightPassengerNeedsDepartureDate, selectAddFlightPassengerStatus } from '_redux/slices/timeline/timeline-bookings';
//icons
import { HiOutlineInformationCircle } from 'react-icons/hi';

const FlightInformationForm = (props) => {

    // manual states
    const [manualCarrier, setManualCarrier] = useState(false);

    // border colors
    const [flightNumberError, setFlightNumberError] = useState(false);
    const [flightNumberValid, setFlightNumberValid] = useState(false);
    const [carrierCodeValid, setCarrierCodeValid] = useState(false);
    const [carrierCodeError, setCarrierCodeError] = useState(false);
    const [departureDateError, setDepartureDateError] = useState(false);
    const [departureDateValid, setDepartureDateValid] = useState(false);
    const [showDepartureDateField, setShowDepartureDateField] = useState(false);      // show departure date field, this might happen for arrival flights in some cases.
    const [departureDate, setDepartureDate] = useState('');                 // departure date for arrival flights
    const [airportCode, setAirportCode] = useState('');                     // origin/destination airport for ambiguous flights (airport accounts)
    const [originAirport, setOriginAirport] = useState('');                 // origin airport for ambiguous flights (airline accounts)
    const [destinationAirport, setDestinationAirport] = useState('');       // destination airport for ambiguous flights (airline accounts)
    const [flightTypeError, setFlightTypeError] = useState(false);
    const [flightTypeValid, setFlightTypeValid] = useState(false)

    // redux
    const dispatch = useDispatch();
    const flightForm = useSelector(selectFlightInformation);
    const needsDepartureDate = useSelector(selectAddFlightPassengerNeedsDepartureDate);                 // true when 422 is returned
    const addFlightPassengerStatus = useSelector(selectAddFlightPassengerStatus);                       // status of addFlightPassenger request

    useEffect(() => {
        return () => {
            setDepartureDate('');
            setAirportCode('');
            setOriginAirport('');
            setDestinationAirport('');
            setShowDepartureDateField(false);
        };
    }, []);

    // error setter
    useEffect(() => {
        if (flightForm.errors['carrierCode']) {
            setCarrierCodeValid(false);
            setCarrierCodeError(flightForm.errors['carrierCode'])
        }
        else {
            setCarrierCodeValid(true);
            setCarrierCodeError(false)
        }
        if (flightForm.errors['flightNumber']) {
            setFlightNumberValid(false);
            setFlightNumberError(flightForm.errors['flightNumber'])
        }
        else {
            setFlightNumberValid(true);
            setFlightNumberError(false)
        }
        if (flightForm.errors['departureDate']) {
            setDepartureDateError(flightForm.errors['departureDate']);
            setDepartureDateValid(false)
        }
        else {
            setDepartureDateError(false);
            setDepartureDateValid(true)
        }

        if (flightForm.errors['flightType']) {
            setFlightTypeError(flightForm.errors['flightType']);
            setFlightTypeValid(false)
        }
        else {
            setFlightTypeError(false);
            setFlightTypeValid(true)
        }

    }, [flightForm]);

    // Show departure date field when 422 is returned (needs departure date)
    useEffect(() => {
        if (needsDepartureDate && addFlightPassengerStatus === 'needs_departure_date') {
            setShowDepartureDateField(true);
        }
    }, [needsDepartureDate, addFlightPassengerStatus]);

    // Reset departure date field when form is reset

    const labelStyle = (hasError) => ({
        fontSize: 13,
        fontWeight: 500,
        color: hasError ? Colors.DARK_RED : Colors.TEXT_COLOR,
        marginBottom: 4,
    });

    const inputStyle = (hasError) => ({
        borderRadius: 8,
        fontSize: 14,
        textTransform: 'uppercase',
        borderColor: hasError ? Colors.DARK_RED : '#dee2e6',
    });

    const errorStyle = {
        color: Colors.DARK_RED,
        fontSize: 12,
        marginTop: 4,
        marginBottom: 0,
    };

    const minDepartureDate = (() => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toISOString().split('T')[0];
    })();

    const additionalAirportLabel = flightForm.flightType === 'INBOUND'
        ? 'Origin Airport'
        : flightForm.flightType === 'OUTBOUND'
            ? 'Destination Airport'
            : 'Origin/Destination Airport';

    const additionalAirportPlaceholder = flightForm.flightType === 'INBOUND'
        ? 'e.g. JFK'
        : flightForm.flightType === 'OUTBOUND'
            ? 'e.g. AMS'
            : 'e.g. JFK';

    const helperTextStyle = {
        color: Colors.GRAY_DARK,
        fontSize: 12,
        marginTop: 4,
        marginBottom: 0,
    };

    const manualRouteWarningMessage = props.isAirline
        ? 'Flight schedule not found. Enter the exact origin and destination IATA codes for the operating route. Incorrect airports will prevent this booking from pairing with the flight and jobs will not appear in the unplanned timeline.'
        : flightForm.flightType === 'INBOUND'
            ? 'Flight schedule not found. Enter the exact departure airport IATA for this flight (where the aircraft left from). A wrong origin (e.g. BOS instead of JFK) means passengers will not show under the correct flight in the timeline.'
            : 'Flight schedule not found. Enter the exact destination airport IATA for this flight. Incorrect airports will prevent this booking from pairing with the flight and jobs will not appear in the unplanned timeline.';

    return (
        <Form onSubmit={(e) => e.preventDefault()}>
            {props.manualFlightDisabled && (
                <div style={{ marginBottom: 12 }}>
                    <Button type="button" variant="outline-primary" size="sm" style={{ borderRadius: 6, fontSize: 13 }} onClick={() => { setManualCarrier(!manualCarrier) }}>
                        Enter Flight Manually
                    </Button>
                </div>
            )}

            {/* Carrier Code & Flight Number */}
            <Form.Group>
                <Row>
                    <Col xs={12} md>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Form.Label style={labelStyle(!carrierCodeValid)}>Carrier Code</Form.Label>
                            {props.manualCarrierDisabled && (
                                <button
                                    type="button"
                                    onClick={() => { setManualCarrier(!manualCarrier) }}
                                    style={{ border: 'none', background: 'none', fontSize: 12, color: Colors.PRM_LIGHT_BLUE, cursor: 'pointer', fontWeight: 500, padding: 0, marginBottom: 4 }}
                                >
                                    {!manualCarrier ? 'Enter manually' : 'Show airline list'}
                                </button>
                            )}
                        </div>
                        {manualCarrier ? (
                            <Form.Control
                                onChange={(e) => { dispatch(setFlightInformation({ ...flightForm, carrierCode: e.target.value })) }}
                                type="name"
                                placeholder="e.g. BA"
                                style={inputStyle(!carrierCodeValid)}
                                value={flightForm.carrierCode}
                                defaultValue={flightForm.carrierCode}
                            />
                        ) : (
                            <CarrierCodeSelector
                                carrierCode={flightForm.carrierCode}
                                setCode={(code) => { dispatch(setFlightInformation({ ...flightForm, carrierCode: code.value })) }}
                                borderColor={!carrierCodeValid ? Colors.DARK_RED : null}
                            />
                        )}
                        {carrierCodeError && <p style={errorStyle}>{carrierCodeError}</p>}
                    </Col>

                    <Col xs={12} md className="mt-3 mt-md-0">
                        <Form.Label style={labelStyle(!flightNumberValid)}>Flight Number</Form.Label>
                        <Form.Control
                            onChange={(e) => { dispatch(setFlightInformation({ ...flightForm, flightNumber: e.target.value })) }}
                            type="name"
                            placeholder="e.g. 1234"
                            style={inputStyle(!flightNumberValid)}
                            value={flightForm.flightNumber}
                            defaultValue={flightForm.flightNumber}
                        />
                        {flightNumberError && <p style={errorStyle}>{flightNumberError}</p>}
                    </Col>
                </Row>
            </Form.Group>

            {/* Departure Date */}
            <Form.Group style={{ marginTop: 16 }}>
                <Row>
                    <Col xs={12} md>
                        <Form.Label style={labelStyle(!departureDateValid)}>flight Date</Form.Label>
                        <Form.Control
                            onChange={(e) => { dispatch(setFlightInformation({ ...flightForm, flightDepartureDate: e.target.value })) }}
                            type="date"
                            style={{ ...inputStyle(!departureDateValid), textTransform: 'none' }}
                            value={flightForm.flightDepartureDate}
                            defaultValue={flightForm.flightDepartureDate}
                            min={minDepartureDate}
                        />
                        {departureDateError && <p style={errorStyle}>{departureDateError}</p>}
                    </Col>
                    <Col xs={12} md className="mt-3 mt-md-0">
                        <Form.Label style={labelStyle(!flightTypeValid)}>Flight Direction</Form.Label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6 }}>
                            {[{ label: 'Inbound', value: 'INBOUND' }, { label: 'Outbound', value: 'OUTBOUND' }].map(opt => (
                                <label
                                    key={opt.value}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 16px',
                                        borderRadius: 8,
                                        border: `1.5px solid ${flightForm.flightType === opt.value ? Colors.PRM_LIGHT_BLUE : '#dee2e6'}`,
                                        background: flightForm.flightType === opt.value ? Colors.PRM_BLUE_ACCENT : '#fff',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: flightForm.flightType === opt.value ? Colors.PRM_LIGHT_BLUE : Colors.TEXT_COLOR,
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <Form.Check
                                        inline
                                        type="radio"
                                        name="flightType"
                                        value={opt.value}
                                        checked={flightForm.flightType === opt.value}
                                        onChange={(e) => dispatch(setFlightInformation({ ...flightForm, flightType: e.target.value }))}
                                        style={{ margin: 0 }}
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                        {flightTypeError && <p style={errorStyle}>{flightTypeError}</p>}
                    </Col>


                </Row>
                {showDepartureDateField && (
                    <div className='mt-4'>
                        <Alert variant="warning" style={{ borderRadius: 8, fontSize: 13 }}>
                            <HiOutlineInformationCircle size={18} style={{ marginRight: 6 }} />
                            {manualRouteWarningMessage}
                        </Alert>
                        <Row>
                            <Col xs={12} md>
                                <Form.Group className='mt-3'>
                                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, fontWeight: 500 }}>
                                        Departure Date <span style={{ color: 'red' }}>*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={departureDate}
                                        min={minDepartureDate}
                                        onChange={(e) => {
                                            setDepartureDate(e.target.value);
                                            props.clearAdditionalFlightDetailsError?.('departureDate');
                                            props.setProvidedDepartureDate(e.target.value);
                                        }}
                                        style={{
                                            borderRadius: 8,
                                            borderColor: props.additionalFlightDetailsErrors?.departureDate ? Colors.DARK_RED : Colors.GRAY_MEDIUM
                                        }}
                                    />
                                    <p style={helperTextStyle}>
                                        Must match the flight&apos;s true departure day. Overnight arrivals often depart the previous calendar day.
                                    </p>
                                    {props.additionalFlightDetailsErrors?.departureDate && <p style={errorStyle}>{props.additionalFlightDetailsErrors.departureDate}</p>}
                                </Form.Group>
                            </Col>
                            {props.isAirline ? (
                                <>
                                    <Col xs={12} md>
                                        <Form.Group className='mt-3'>
                                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, fontWeight: 500 }}>
                                                Origin Airport <span style={{ color: 'red' }}>*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={originAirport}
                                                maxLength={3}
                                                placeholder="e.g. JFK"
                                                onChange={(e) => {
                                                    const value = e.target.value.toUpperCase();
                                                    setOriginAirport(value);
                                                    props.clearAdditionalFlightDetailsError?.('originAirport');
                                                    props.setProvidedOriginAirport?.(value);
                                                }}
                                                style={{
                                                    ...inputStyle(props.additionalFlightDetailsErrors?.originAirport),
                                                    borderColor: props.additionalFlightDetailsErrors?.originAirport ? Colors.DARK_RED : Colors.GRAY_MEDIUM
                                                }}
                                            />
                                            <p style={helperTextStyle}>
                                                Must match the flight&apos;s real departure airport. Incorrect codes cannot be corrected later.
                                            </p>
                                            {props.additionalFlightDetailsErrors?.originAirport && <p style={errorStyle}>{props.additionalFlightDetailsErrors.originAirport}</p>}
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md>
                                        <Form.Group className='mt-3'>
                                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, fontWeight: 500 }}>
                                                Destination Airport <span style={{ color: 'red' }}>*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={destinationAirport}
                                                maxLength={3}
                                                placeholder="e.g. AMS"
                                                onChange={(e) => {
                                                    const value = e.target.value.toUpperCase();
                                                    setDestinationAirport(value);
                                                    props.clearAdditionalFlightDetailsError?.('destinationAirport');
                                                    props.setProvidedDestinationAirport?.(value);
                                                }}
                                                style={{
                                                    ...inputStyle(props.additionalFlightDetailsErrors?.destinationAirport),
                                                    borderColor: props.additionalFlightDetailsErrors?.destinationAirport ? Colors.DARK_RED : Colors.GRAY_MEDIUM
                                                }}
                                            />
                                            <p style={helperTextStyle}>
                                                Must match the flight&apos;s arrival airport. Incorrect codes cannot be corrected later.
                                            </p>
                                            {props.additionalFlightDetailsErrors?.destinationAirport && <p style={errorStyle}>{props.additionalFlightDetailsErrors.destinationAirport}</p>}
                                        </Form.Group>
                                    </Col>
                                </>
                            ) : (
                                <Col xs={12} md>
                                    <Form.Group className='mt-3'>
                                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, fontWeight: 500 }}>
                                            {additionalAirportLabel} <span style={{ color: 'red' }}>*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={airportCode}
                                            maxLength={3}
                                            placeholder={additionalAirportPlaceholder}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                setAirportCode(value);
                                                props.clearAdditionalFlightDetailsError?.('airportCode');
                                                props.setProvidedAirportCode?.(value);
                                            }}
                                            style={{
                                                ...inputStyle(props.additionalFlightDetailsErrors?.airportCode),
                                                borderColor: props.additionalFlightDetailsErrors?.airportCode ? Colors.DARK_RED : Colors.GRAY_MEDIUM
                                            }}
                                        />
                                        <p style={helperTextStyle}>
                                            {flightForm.flightType === 'INBOUND'
                                                ? 'Must match the flight\'s real departure airport. Incorrect codes cannot be corrected later.'
                                                : 'Must match the flight\'s arrival airport. Incorrect codes cannot be corrected later.'}
                                        </p>
                                        {props.additionalFlightDetailsErrors?.airportCode && <p style={errorStyle}>{props.additionalFlightDetailsErrors.airportCode}</p>}
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </div>
                )}
            </Form.Group>
        </Form>
    );
};

export default FlightInformationForm;
